import { useShallow } from 'zustand/react/shallow';

import { ReportOrgan, USER_PREFERENCE_KEY } from '@/api';
import {
  useFctReport,
  useFindingsByOrgan,
  useIsQualityReviewApplicable,
  useUserPreferences,
} from '@/shared/hook';
import { REPORT_LINK_KEY, ReportLink, ReportLinkKey } from '@/shared/model';
import { excludeRemovedFindings, isEmpty, mergeFindings } from '@/shared/util';
import {
  ReportValidationStatus,
  useFinalImpressionsFormStore,
  useReportValidationStore,
} from '@/store';

import { hasMissingPriorScanComparison } from '../../organ/organ.util';
import {
  evaluateGroupedFindingsForMissingFields,
  hasFindingsWithInvalidScores,
  validateOrgans,
} from '../../preview/preview.util';
import { isOrganDisabled } from '../util';
import { useAddendumTextForValidation } from './use-addendum-text-for-validation.hook';
import { useOrgansWithDraftReportFindingLesions } from './use-organs-with-draft-report-finding-lesions.hook';
import { usePreviousStudies } from './use-previous-studies.hook';
import { useUpdateKeyImagesStatus } from './use-update-key-images-status.hook';
import { useValidateFinalImpression } from './use-validate-final-impression.hook';
import { useValidateQualityControl } from './use-validate-quality-control.hook';

interface ValidateReportResult {
  hasInvalidScores: boolean;
  hasMissingFields: boolean;
  isValid: boolean;
}

export function useValidateReport(
  links: ReportLink[],
): (
  value: Record<string, ReportValidationStatus>,
  reportOrgans: Record<string, Record<string, ReportOrgan>>,
) => ValidateReportResult {
  const { defaultFindings, findings } = useFindingsByOrgan();
  const { report } = useFctReport();
  const addendumForValidation = useAddendumTextForValidation();
  const { defaultQuestionStatus, setStatus } = useReportValidationStore(
    useShallow((state) => ({
      defaultQuestionStatus: state.defaultQuestionStatus,
      setStatus: state.setStatus,
    })),
  );

  const { finalImpressions, nextRecommendedScan, urgencyCategory } = useFinalImpressionsFormStore(
    useShallow((state) => ({
      finalImpressions: state.finalImpressions,
      nextRecommendedScan: state.nextRecommendedScan,
      urgencyCategory: state.urgencyCategory,
    })),
  );

  const validateFinalImpression = useValidateFinalImpression();
  const isQualityReviewApplicable = useIsQualityReviewApplicable();
  const isQualityReviewValid = useValidateQualityControl();
  const validateKeyImages = useUpdateKeyImagesStatus();

  const organsWithDraftLesions = useOrgansWithDraftReportFindingLesions(links);
  const { hasPreviousStudyResults, isError: isPriorStudiesError } = usePreviousStudies();
  const { getPreference } = useUserPreferences();

  return (
    value: Record<ReportLinkKey, ReportValidationStatus>,
    reportOrgans: Record<string, Record<string, ReportOrgan>>,
  ): ValidateReportResult => {
    const organs = { ...value };

    if (isEmpty(links) || !report) {
      const isValid = Object.values(organs).every((status) => !status.error);

      return {
        hasInvalidScores: false,
        hasMissingFields: false,
        isValid,
      };
    }

    const mergedFindings = excludeRemovedFindings(mergeFindings(links, defaultFindings, findings));

    const missingRequiredFields = evaluateGroupedFindingsForMissingFields(
      mergedFindings,
      links,
      reportOrgans[report?.uuid ?? ''],
    );

    const hasMissingFields = Object.entries(missingRequiredFields).some(
      ([organKey, field]) => field.error && !isOrganDisabled(organKey as ReportLinkKey, links),
    );

    const { hasInvalidScores, organsWithInvalidScores } = hasFindingsWithInvalidScores(
      mergedFindings,
      links,
    );
    const invalidScoresSet = new Set(organsWithInvalidScores);

    const keyImagesHaveError = validateKeyImages();

    const currentFinalImpressions = finalImpressions || report.finalImpressions;
    const currentNextRecommendedScan = nextRecommendedScan ?? report.nextRecommendedScan;
    const currentUrgencyCategory = urgencyCategory ?? report.urgencyCategory;

    const currentReport = {
      ...report,
      finalImpressions: currentFinalImpressions,
      nextRecommendedScan: currentNextRecommendedScan,
      priorStudyIds: report.priorStudyIds,
      urgencyCategory: currentUrgencyCategory,
    };

    const isFinalImpressionsInvalid = Object.values(
      validateFinalImpression(currentReport, addendumForValidation),
    ).some(Boolean);

    const defaultFindingStatus =
      (getPreference(USER_PREFERENCE_KEY.COMPARISON_STATUS_DEFAULT)?.currentValue as
        boolean | undefined) ?? true;
    const validateComparisonStatus = !defaultFindingStatus;

    validateOrgans({
      defaultQuestionStatus,
      hasPreviousStudyResults: hasPreviousStudyResults || isPriorStudiesError,
      invalidScoresSet,
      links,
      mergedFindings,
      missingRequiredFields,
      organs,
      organsWithDraftLesions,
      setStatus,
      validateComparisonStatus,
    });

    const hasMissingComparison =
      (hasPreviousStudyResults || isPriorStudiesError) &&
      Object.entries(mergedFindings).some(([organKey, organFindings]) => {
        if (isOrganDisabled(organKey, links)) return false;

        return hasMissingPriorScanComparison(organFindings, {
          validateStatus: validateComparisonStatus,
        });
      });

    if (isFinalImpressionsInvalid) {
      organs.final_impressions = { ...organs.final_impressions, error: true };

      setStatus(REPORT_LINK_KEY.FINAL_IMPRESSIONS, organs.final_impressions);
    } else {
      organs.final_impressions = { ...organs.final_impressions, error: false };

      setStatus(REPORT_LINK_KEY.FINAL_IMPRESSIONS, organs.final_impressions);
    }

    if (keyImagesHaveError) {
      organs.key_images = { ...organs.key_images, error: true };

      setStatus(REPORT_LINK_KEY.KEY_IMAGES, organs.key_images);
    } else {
      organs.key_images = { ...organs.key_images, error: false };

      setStatus(REPORT_LINK_KEY.KEY_IMAGES, organs.key_images);
    }

    if (isQualityReviewApplicable) {
      if (isQualityReviewValid) {
        organs.quality_review = { ...organs.quality_review, error: false };

        setStatus(REPORT_LINK_KEY.QUALITY_REVIEW, organs.quality_review);
      } else {
        organs.quality_review = { ...organs.quality_review, error: true };

        setStatus(REPORT_LINK_KEY.QUALITY_REVIEW, organs.quality_review);
      }
    }

    const isValid = Object.values(organs).every((status) => !status.error);

    const hasQualityReviewMissing = isQualityReviewApplicable && !isQualityReviewValid;

    return {
      hasInvalidScores,
      hasMissingFields: hasMissingFields || hasQualityReviewMissing || hasMissingComparison,
      isValid,
    };
  };
}
